import { Badge, ProgressBar } from "react-bootstrap";
import { getRelativeTime } from "../../../../app/dateHelpers";
import { Icon, IconDefinitions } from "../../../../components";
import { UploadSessionModel } from "../../../../data/entities";
import { getDurationLabel, getFileName, getProgress } from "../../helper";
import { pillBadge } from "./styles";
import { UserService } from "../../../../services";
import { Roles } from "../../../../models";

type SessionStatus = "active" | "passed" | "warning" | "alert" | "error";

interface Props {
  session: UploadSessionModel;
  status: SessionStatus;
  onViewResults?: (session: UploadSessionModel) => void;
}

const STATUS_CONFIG: Record<
  Exclude<SessionStatus, "active">,
  {
    icon: string;
    iconClass: string;
    badgeBg: string;
    label: string;
  }
> = {
  passed: {
    icon: IconDefinitions.circleCheck,
    iconClass: "text-success",
    badgeBg: "success",
    label: "Passed",
  },
  warning: {
    icon: IconDefinitions.triangleExclamation,
    iconClass: "text-warning",
    badgeBg: "warning",
    label: "Warning",
  },
  alert: {
    icon: IconDefinitions.triangleExclamation,
    iconClass: "text-danger",
    badgeBg: "danger",
    label: "Alert",
  },
  error: {
    icon: IconDefinitions.triangleExclamation,
    iconClass: "text-danger",
    badgeBg: "danger",
    label: "Error",
  },
};

export function SessionCard({ session, status, onViewResults }: Props) {
  const fileName = getFileName(session.filePath);
  const percent = getProgress(session);
  const isQueued = session.status === "Queued";
  const isStaff = UserService.isInRole(Roles.admin) || UserService.isInRole(Roles.staff) || UserService.isInRole(Roles.dataAnalyst);

  // ── Active / Queued ──
  if (status === "active") {
    return (
      <div className="col-md-12">
        <div
          className="card border-0 h-100"
          style={{
            backgroundColor: "var(--bs-light)",
            transition: "opacity 0.2s ease",
          }}
        >
          <div className="card-body p-3">
            {/* Header */}
            <div className="d-flex align-items-start justify-content-between mb-2">
              <div
                className="d-flex align-items-center"
                style={{ minWidth: 0 }}
              >
                <Icon
                  iconName={IconDefinitions.fileAlt}
                  className={`me-2 ${isQueued ? "text-warning" : "text-primary"} flex-shrink-0`}
                />
                <span className="fw-semibold text-truncate">{fileName}</span>
              </div>
              <div className="d-flex align-items-center flex-shrink-0 ms-2">
                {isQueued ? (
                  <Badge bg="warning" style={pillBadge}>
                    Queued
                  </Badge>
                ) : (
                  <Badge bg="primary" style={pillBadge}>
                    {percent}%
                  </Badge>
                )}
              </div>
            </div>

            {/* Inspection info */}
            {session.inspectionId && (
              <div className="small text-muted mb-2">
                <Icon iconName={IconDefinitions.clipboard} className="me-1" />
                {session.inspection.reference}
              </div>
            )}

            {/* Progress bar */}
            <div className="mb-2">
              {isQueued ? (
                <>
                  <ProgressBar
                    animated
                    now={100}
                    variant="warning"
                    style={{ height: "6px" }}
                  />
                  <small className="text-muted mt-1 d-block">
                    Waiting to start...
                  </small>
                </>
              ) : (
                <>
                  <ProgressBar
                    animated
                    now={percent}
                    variant="primary"
                    style={{ height: "6px" }}
                  />
                  <div className="d-flex justify-content-between mt-1">
                    {isStaff && session.uploadedChunks > 0 && (
                      <small className="text-muted">
                        {session.uploadedChunks?.toLocaleString()} /{" "}
                        {session.totalChunks?.toLocaleString()} Rows
                      </small>
                    )}
                    {session.lastUploadAt && (
                      <small className="text-muted ms-auto">
                        Last update{" "}
                        {getRelativeTime(new Date(session.lastUploadAt))}
                      </small>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top">
              <small className="text-muted">
                <Icon iconName={IconDefinitions.clock} className="me-1" />
                Started {getRelativeTime(new Date(session.startDate))}
              </small>
              <small className="text-muted">
                {(session.fileSizeBytes / 1024 / 1024).toFixed(1)} MB
              </small>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── History cards: passed / warning / failed / error ──
  const config = STATUS_CONFIG[status];
  const hadProgress = session.uploadedChunks > 0;
  const isError = status === "error";

  return (
    <div
      className="card border h-100"
      style={{ transition: "opacity 0.2s ease" }}
    >
      <div className="card-body p-3">
        {/* Header */}
        <div className="d-flex align-items-start justify-content-between mb-2">
          <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
            <Icon
              iconName={config.icon}
              className={`me-2 ${config.iconClass} flex-shrink-0`}
            />
            <span className="fw-semibold text-truncate">{fileName}</span>
          </div>
          <Badge bg={config.badgeBg} style={pillBadge}>
            {config.label}
            {isError && hadProgress ? ` · ${percent}%` : ""}
          </Badge>
        </div>

        {/* Inspection info */}
        {session.inspectionId && (
          <div className="small text-muted mb-2">
            <Icon iconName={IconDefinitions.clipboard} className="me-1" />
            {session.inspection.reference}
          </div>
        )}

        {/* Error message (only for error status) */}
        {isError && session.errorMessage && (
          <div
            className="rounded-2 p-2 mb-2"
            style={{ backgroundColor: "#fef2f2" }}
          >
            <code
              className="d-block"
              style={{
                fontSize: "0.79rem",
                fontWeight: 500,
                lineHeight: 1.55,
                color: "#b91c1c",
                wordBreak: "break-word",
              }}
            >
              {session.errorMessage}
            </code>
          </div>
        )}

        {/* Stats row */}
        {(!isError || hadProgress) && (
          <div className="d-flex gap-2 my-2">
            {isStaff && (
            <div
              className="flex-fill text-center py-2 px-2 rounded"
              style={{ backgroundColor: "#f9fafb" }}
            >
              <div
                className="text-muted text-uppercase"
                style={{
                  fontSize: "0.65rem",
                  letterSpacing: "0.04em",
                  fontWeight: 500,
                }}
              >
                {isError ? "Processed" : "Total Rows Processed"}
              </div>
              <div
                className="fw-semibold"
                style={{ fontSize: "0.82rem", color: "#374151" }}
              >
                {isError
                  ? `${session.uploadedChunks?.toLocaleString()} / ${session.totalChunks?.toLocaleString()}`
                  : session.totalChunks?.toLocaleString()}
              </div>
            </div>
            )}
            {!isError && (
              <div
                className="flex-fill text-center py-2 px-2 rounded"
                style={{ backgroundColor: "#f9fafb" }}
              >
                <div
                  className="text-muted text-uppercase"
                  style={{
                    fontSize: "0.65rem",
                    letterSpacing: "0.04em",
                    fontWeight: 500,
                  }}
                >
                  Duration
                </div>
                <div
                  className="fw-semibold"
                  style={{ fontSize: "0.82rem", color: "#374151" }}
                >
                  {getDurationLabel(session.startDate, session.endDate!)}
                </div>
              </div>
            )}
            <div
              className="flex-fill text-center py-2 px-2 rounded"
              style={{ backgroundColor: "#f9fafb" }}
            >
              <div
                className="text-muted text-uppercase"
                style={{
                  fontSize: "0.65rem",
                  letterSpacing: "0.04em",
                  fontWeight: 500,
                }}
              >
                Size
              </div>
              <div
                className="fw-semibold"
                style={{ fontSize: "0.82rem", color: "#374151" }}
              >
                {(session.fileSizeBytes / 1024 / 1024).toFixed(1)} MB
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="d-flex justify-content-between align-items-center mt-2">
          <small
            className="text-muted"
            title={new Date(session.endDate!).toLocaleString()}
          >
            <Icon iconName={IconDefinitions.clock} className="me-1" />
            {isError ? "Failed" : "Finished"}{" "}
            {getRelativeTime(new Date(session.endDate!))}
          </small>
          {!isError && onViewResults && (
            <button
              className="btn btn-sm btn-outline-dark"
              onClick={() => onViewResults(session)}
            >
              View Results
              <Icon iconName={IconDefinitions.arrowRight} className="ms-1" />
            </button>
          )}
          {isError && !hadProgress && (
            <small className="text-muted">
              {(session.fileSizeBytes / 1024 / 1024).toFixed(1)} MB
            </small>
          )}
        </div>
      </div>
    </div>
  );
}
